import {Box, Group, Paper, Progress, SegmentedControl, Stack, Text} from '@mantine/core';
import {useLocalStorage} from '@mantine/hooks';
import type {RegistrationsStat} from '../../client';

type RegistrationProgressProps = {
    stats: RegistrationsStat[] | undefined;
};

type StatusKey = 'paid' | 'unpaid' | 'delayed' | 'staff';

type ViewMode = 'detail' | 'simple';

type LegendItem = {
    label: string;
    count: number;
    status: StatusKey;
};

type MainBarSegment = {
    count: number;
    color: string;
};

const VIEW_MODE_STORAGE_KEY = 'registration-progress-view-mode';

/** Matches row highlights in Registrations.tsx (shade 8; unpaid uses pangolin orange). */
export const REGISTRATION_STATUS_COLORS: Record<StatusKey, string> = {
    paid: '#2f9e44',
    unpaid: '#E07B39',
    delayed: '#e03131',
    staff: '#6741d9',
};

function statValue(stats: RegistrationsStat[] | undefined, name: string): number {
    return stats?.find((item) => item.name === name)?.value ?? 0;
}

function segmentPct(count: number, max: number): number {
    return max > 0 && count > 0 ? (count / max) * 100 : 0;
}

function buildMainBarSegments(
    viewMode: ViewMode,
    paid: number,
    outstanding: number,
    delayed: number,
): MainBarSegment[] {
    if (viewMode === 'simple') {
        const unpaidOrDelayed = outstanding + delayed;
        return [
            {count: paid, color: REGISTRATION_STATUS_COLORS.paid},
            {count: unpaidOrDelayed, color: REGISTRATION_STATUS_COLORS.unpaid},
        ];
    }

    return [
        {count: paid, color: REGISTRATION_STATUS_COLORS.paid},
        {count: outstanding, color: REGISTRATION_STATUS_COLORS.unpaid},
        {count: delayed, color: REGISTRATION_STATUS_COLORS.delayed},
    ];
}

function buildLegendItems(
    viewMode: ViewMode,
    paid: number,
    outstanding: number,
    delayed: number,
    staff: number,
): LegendItem[] {
    if (viewMode === 'simple') {
        return [
            {label: 'Paid', count: paid, status: 'paid'},
            {label: 'Unpaid / Delayed', count: outstanding + delayed, status: 'unpaid'},
            {label: 'Staff', count: staff, status: 'staff'},
        ];
    }

    return [
        {label: 'Paid', count: paid, status: 'paid'},
        {label: 'Unpaid', count: outstanding, status: 'unpaid'},
        {label: 'Delayed', count: delayed, status: 'delayed'},
        {label: 'Staff', count: staff, status: 'staff'},
    ];
}

function LegendSwatch({status}: {status: StatusKey}) {
    return (
        <Box
            w={12}
            h={12}
            style={{
                backgroundColor: REGISTRATION_STATUS_COLORS[status],
                borderRadius: 2,
                flexShrink: 0,
            }}
        />
    );
}

export function RegistrationProgress({stats}: RegistrationProgressProps) {
    const [viewMode, setViewMode] = useLocalStorage<ViewMode>({
        key: VIEW_MODE_STORAGE_KEY,
        defaultValue: 'detail',
    });

    const registered = statValue(stats, 'Total');
    const max = statValue(stats, 'Max');
    const remaining = statValue(stats, 'Free');
    const paid = statValue(stats, 'Paid');
    const outstanding = statValue(stats, 'Outstanding');
    const delayed = statValue(stats, 'Delayed');
    const staff = statValue(stats, 'Staff');

    const mainBarSegments = buildMainBarSegments(viewMode, paid, outstanding, delayed);
    const legendItems = buildLegendItems(viewMode, paid, outstanding, delayed, staff);

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="xs">
                <Group justify="space-between" align="center">
                    <Text size="sm" fw={600}>Registration status</Text>
                    <SegmentedControl
                        size="xs"
                        value={viewMode}
                        onChange={(value) => setViewMode(value as ViewMode)}
                        data={[
                            {label: 'Detail', value: 'detail'},
                            {label: 'Simple', value: 'simple'},
                        ]}
                    />
                </Group>
                <Progress.Root size="lg" radius="xl">
                    {mainBarSegments.map(({count, color}, index) => (
                        count > 0 ? (
                            <Progress.Section
                                key={index}
                                value={segmentPct(count, max)}
                                color={color}
                            />
                        ) : null
                    ))}
                </Progress.Root>
                {staff > 0 && (
                    <Progress.Root size="sm" radius="xl">
                        <Progress.Section
                            value={segmentPct(staff, max)}
                            color={REGISTRATION_STATUS_COLORS.staff}
                        />
                    </Progress.Root>
                )}
                <Group gap="lg">
                    {legendItems.map(({label, count, status}) => (
                        <Group key={label} gap={6}>
                            <LegendSwatch status={status}/>
                            <Text size="sm" c="dimmed">{label} ({count})</Text>
                        </Group>
                    ))}
                </Group>
                <Text size="sm" c="dimmed">
                    {registered} of {max} registered ({remaining} remaining)
                </Text>
            </Stack>
        </Paper>
    );
}
